/**
 * TabellenPage — AHB/MIG-Tabellen-Browser mit zwei Modi:
 *
 *  - "Einzeldokument": FV + Doctype (AHB|MIG) + Format (+ Prüfid bei AHB) wählen,
 *    Dokument laden, Metadaten-Header-Card + Tabelle rendern, CSV-Export.
 *  - "Globale Suche": FV + Format (AHB) wählen, ALLE Prüfid-Docs lazy laden
 *    (gecacht per fv|fmt), flachen Zeilen-Index bauen und client-seitig über
 *    mehrere Feld-Filter durchsuchen.
 *
 * Daten kommen ausschließlich aus `regelwerkApi` (maco-runner Doc-Routen).
 *
 * **Zwei Achsen, eine davon global (AP-B).** Die *Formatversion* (`fv`) folgt
 * der Kopfzeile — `useFvBinding` regelt Vorrang und Rückschreiben. Der *Stand*
 * (`stand`, die Ausgabe innerhalb einer FV) bleibt bewusst lokal: er existiert
 * nur innerhalb seiner Formatversion und wird beim FV-Wechsel ohnehin
 * zurückgesetzt (siehe den Effekt unten). Global gehalten wäre er unbedienbar.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Card,
  Select,
  SelectItem,
  Button,
  Icon,
  Notice,
  SegmentedControl,
  Segment,
} from "@conuti-das/prince-ui";
import { Flex, Spinner, princeIcon } from "@/ui-prince";
import { regelwerkApi } from "@/lib/regelwerkApi";
import type { AhbDoc, AhbLine, MigDoc, MigSegment, MigDataElement } from "@/types/regelwerk";
import artifactsData from "@/data/generated/artifacts.json";
import type { ArtifactIndex, StandEntry, StandIndex } from "@/types";
import { FvNote, useFvBinding } from "./fvBinding";
import "./tabellen.css";

// ---------------------------------------------------------------------------
// Helpers (pure — unit-testable)
// ---------------------------------------------------------------------------

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/** "202610" → "Oktober 2026 (FV2610)" */
export function releaseLabel(release: string): string {
  const m = /^(\d{4})(\d{2})$/.exec(release);
  if (!m) return release;
  const month = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${month} ${m[1]} (FV${release.slice(2)})`;
}

/** Stand-Einträge des gewählten Formats aus einem FV-Stand-Index (leer, wenn keine). */
export function standOptions(
  idx: Record<string, StandIndex> | undefined,
  format: string,
): StandEntry[] {
  return idx?.[format]?.staende ?? [];
}

/** A single flattened MIG row, ready for table rendering / CSV export. */
export interface MigFlatRow {
  /** depth in the segment/group tree (0 = top-level segment or group) */
  level: number;
  /** "segment" | "group" | "data_element" | "composite" */
  kind: string;
  /** segment-/group-/data-element-code, e.g. "BGM", "SG2", "1001" */
  code: string;
  name: string;
  status: string;
  format: string;
  /** joined `codes[].value` for data elements, "" otherwise */
  codes: string;
}

function pick(...vals: Array<string | undefined>): string {
  for (const v of vals) if (v != null && v !== "") return v;
  return "";
}

function joinCodes(codes: MigDataElement["codes"]): string {
  if (!codes || codes.length === 0) return "";
  return codes
    .map((c) => pick(c.value, c.name))
    .filter(Boolean)
    .join(", ");
}

function flattenDataElements(
  des: MigDataElement[] | undefined,
  level: number,
  out: MigFlatRow[],
): void {
  if (!des) return;
  for (const de of des) {
    out.push({
      level,
      kind: de.kind ?? "data_element",
      code: pick(de.data_element),
      name: pick(de.name),
      status: pick(de.status_spec, de.status_std),
      format: pick(de.format_spec, de.format_std),
      codes: joinCodes(de.codes),
    });
    // composites nest further data elements
    flattenDataElements(de.data_elements, level + 1, out);
  }
}

/**
 * Recursively flatten the MIG segment/group tree into indented rows.
 * Order: a node, then its data elements, then its child segments (groups).
 * `level` carries nesting depth for indentation.
 */
export function flattenMigSegments(
  segments: MigSegment[] | undefined,
  level = 0,
  out: MigFlatRow[] = [],
): MigFlatRow[] {
  if (!segments) return out;
  for (const seg of segments) {
    out.push({
      level,
      kind: seg.kind ?? "segment",
      code: pick(seg.segment_code, seg.counter, seg.number),
      name: pick(seg.name),
      status: pick(seg.status_spec, seg.status_std),
      format: "",
      codes: "",
    });
    flattenDataElements(seg.data_elements, level + 1, out);
    // group nodes nest child segments
    flattenMigSegments(seg.segments, level + 1, out);
  }
  return out;
}

// ---------------------------------------------------------------------------
// CSV export (reuses the ConditionsOverview Blob pattern)
// ---------------------------------------------------------------------------

function csvCell(v: string): string {
  const s = v ?? "";
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, header: string[], rows: string[][]): void {
  const lines = [header, ...rows].map((r) => r.map(csvCell).join(","));
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Global-search flat index
// ---------------------------------------------------------------------------

interface SearchRow {
  fv: string;
  format: string;
  pruefid: string;
  segment_group_key: string;
  segment_code: string;
  data_element: string;
  value_pool_entry: string;
  ahb_expression: string;
  name: string;
  conditions: string;
}

function ahbLineToSearchRow(
  line: AhbLine,
  fv: string,
  format: string,
  pruefid: string,
): SearchRow {
  return {
    fv,
    format,
    pruefid,
    segment_group_key: line.segment_group_key ?? "",
    segment_code: line.segment_code ?? "",
    data_element: line.data_element ?? "",
    value_pool_entry: line.value_pool_entry ?? "",
    ahb_expression: line.ahb_expression ?? "",
    name: line.name ?? "",
    conditions: line.conditions ?? "",
  };
}

// ---------------------------------------------------------------------------
// Condition references — tooltip + jump into the Bedingungsbaum
// ---------------------------------------------------------------------------

/** id→text resolver over a doc's pruefungen + pakete (package cardinality
 *  suffixes like `9P0..1` are stripped for the lookup). */
export function makeConditionLookup(doc: AhbDoc | null): (id: string) => string | undefined {
  const map = new Map<string, string>();
  for (const c of doc?.pruefungen ?? []) map.set(c.id, c.text);
  for (const p of doc?.pakete ?? []) map.set(p.id, p.text);
  return (id: string) => map.get(id) ?? map.get(id.replace(/\d+\.\.(?:\d+|n)$/i, ""));
}

/**
 * Renders an AHB expression with each `[…]` reference as a clickable chip:
 * tooltip = resolved condition text, click = jump into the Bedingungsbaum with
 * the full expression of this line pre-seeded (deep-link via `expr` param).
 */
function ExprWithRefs({
  expr,
  fv,
  fmt,
  pruefid,
  lookup,
}: {
  expr: string;
  fv: string;
  fmt: string;
  pruefid: string;
  lookup?: (id: string) => string | undefined;
}) {
  if (!expr) return null;
  const parts = expr.split(/(\[[^\]]+\])/g);
  if (parts.length === 1) return <>{expr}</>;
  const href = `/regelwerk/bedingungen?fv=${encodeURIComponent(fv)}&fmt=${encodeURIComponent(fmt)}&pruefid=${encodeURIComponent(pruefid)}&expr=${encodeURIComponent(expr)}`;
  return (
    <>
      {parts.map((part, i) => {
        const m = /^\[([^\]]+)\]$/.exec(part);
        if (!m) return <span key={i}>{part}</span>;
        const text = lookup?.(m[1]);
        return (
          <Link
            key={i}
            to={href}
            className="tabellen-cond-ref"
            title={text ? `[${m[1]}] ${text} — im Bedingungsbaum öffnen` : `[${m[1]}] im Bedingungsbaum öffnen`}
          >
            {part}
          </Link>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------

type Doctype = "ahb" | "mig";
type Mode = "single" | "search";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TabellenPage() {
  const [params, setParams] = useSearchParams();

  const [mode, setMode] = useState<Mode>(
    params.get("mode") === "search" ? "search" : "single",
  );

  // ---- shared: list of formatversions -------------------------------------
  // Die FV-Achse hängt an der Kopfzeile; der Stand weiter unten bleibt lokal.
  const [fvs, setFvs] = useState<string[]>([]);
  const { fv, setFv, seedFv, note } = useFvBinding(fvs, params.get("fv"));
  const [indexError, setIndexError] = useState<string | null>(null);

  useEffect(() => {
    regelwerkApi
      .getDocIndex()
      .then((idx) => {
        const ahbFvs = idx.doctypes?.ahb?.formatversions ?? [];
        const migFvs = idx.doctypes?.mig?.formatversions ?? [];
        const all = Array.from(new Set([...ahbFvs, ...migFvs])).sort().reverse();
        setFvs(all);
        seedFv(all[0] ?? "");
      })
      .catch((e) => setIndexError(String(e)));
  }, [seedFv]);

  // ---- docs of the selected FV (formats + pruefids) -----------------------
  const [docsAhb, setDocsAhb] = useState<Record<string, string[]>>({});
  const [docsMig, setDocsMig] = useState<string[]>([]);
  const [docsError, setDocsError] = useState<string | null>(null);

  useEffect(() => {
    if (!fv) return;
    setDocsError(null);
    regelwerkApi
      .listDocs(fv)
      .then((d) => {
        setDocsAhb(d.ahb ?? {});
        setDocsMig(d.mig ?? []);
      })
      .catch((e) => {
        setDocsAhb({});
        setDocsMig([]);
        setDocsError(String(e));
      });
  }, [fv]);

  // ---- reflect FV + mode in the URL (cheap deep-link) ---------------------
  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("mode", mode);
    if (fv) next.set("fv", fv);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fv]);

  return (
    <div className="page tabellen-page">
      <div className="page-header">
        <h2>AHB/MIG-Tabellen</h2>
        <div className="page-subtitle">
          Einzeldokument-Tabellen mit Metadaten-Header und CSV-Export, plus eine
          globale Mehrfeld-Suche über alle Prüfidentifikatoren eines AHB-Formats.
        </div>
      </div>

      <Flex className="tabellen-modebar" alignItems="Center">
        <SegmentedControl
          aria-label="Anzeigemodus"
          selectedKeys={new Set([mode])}
          onSelectionChange={(keys) => {
            const first = [...keys][0];
            if (first != null) setMode(String(first) as Mode);
          }}
        >
          <Segment id="single">Einzeldokument</Segment>
          <Segment id="search">Globale Suche</Segment>
        </SegmentedControl>
      </Flex>

      {indexError && (
        <div style={{ marginBottom: "var(--s-3)" }}>
          <Notice tone="negative">Doc-Index konnte nicht geladen werden: {indexError}</Notice>
        </div>
      )}
      {docsError && (
        <div style={{ marginBottom: "var(--s-3)" }}>
          <Notice tone="negative">Dokumentliste konnte nicht geladen werden: {docsError}</Notice>
        </div>
      )}

      <FvNote note={note} />


      {mode === "single" ? (
        <SingleDocMode
          fvs={fvs}
          fv={fv}
          setFv={setFv}
          docsAhb={docsAhb}
          docsMig={docsMig}
          params={params}
          setParams={setParams}
        />
      ) : (
        <GlobalSearchMode fvs={fvs} fv={fv} setFv={setFv} docsAhb={docsAhb} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode A — Einzeldokument
// ---------------------------------------------------------------------------

function FvSelect({
  fvs,
  fv,
  setFv,
}: {
  fvs: string[];
  fv: string;
  setFv: (v: string) => void;
}) {
  return (
    <Flex className="tabellen-field" alignItems="Center">
      <label htmlFor="tab-fv">Formatversion</label>
      <Select
        id="tab-fv"
        style={{ minWidth: "220px" }}
        selectedKey={fv || null}
        onSelectionChange={(key) => {
          if (key) setFv(String(key));
        }}
      >
        {fvs.map((r) => (
          <SelectItem key={r} id={r}>
            {releaseLabel(r)}
          </SelectItem>
        ))}
      </Select>
    </Flex>
  );
}

function SingleDocMode({
  fvs,
  fv,
  setFv,
  docsAhb,
  docsMig,
  params,
  setParams,
}: {
  fvs: string[];
  fv: string;
  setFv: (v: string) => void;
  docsAhb: Record<string, string[]>;
  docsMig: string[];
  params: URLSearchParams;
  setParams: ReturnType<typeof useSearchParams>[1];
}) {
  const [doctype, setDoctype] = useState<Doctype>(
    params.get("doctype") === "mig" ? "mig" : "ahb",
  );
  const [format, setFormat] = useState<string>(params.get("format") ?? "");
  const [pruefid, setPruefid] = useState<string>(params.get("pruefid") ?? "");

  const ahbFormats = useMemo(() => Object.keys(docsAhb).sort(), [docsAhb]);
  const migFormats = useMemo(() => [...docsMig].sort(), [docsMig]);
  const formats = doctype === "ahb" ? ahbFormats : migFormats;
  const pruefids = useMemo(
    () => (format ? docsAhb[format] ?? [] : []),
    [docsAhb, format],
  );

  // ---- Stände des gewählten Formats (build-time Artefakt-Index) ------------
  const artifacts = artifactsData as ArtifactIndex;
  const standIdx =
    doctype === "ahb" ? artifacts.ahbStaende?.[fv] : artifacts.migStaende?.[fv];
  const staende = standOptions(standIdx, format);
  const current = standIdx?.[format]?.current ?? "";
  const [stand, setStand] = useState<string>(params.get("stand") ?? "");
  const effectiveStand =
    stand && staende.some((s) => s.stand === stand) ? stand : current;
  const isCurrent = !current || effectiveStand === current;

  // Format-/FV-/Doctype-Wechsel → Stand zurücksetzen
  useEffect(() => {
    setStand("");
  }, [fv, format, doctype]);

  // keep format/pruefid valid as FV / doctype / format changes.
  // Solange die Doc-Listen noch leer sind (initialer Load), nichts zurücksetzen —
  // sonst werden deep-gelinkte URL-Parameter weggewischt, bevor die Listen da sind.
  useEffect(() => {
    if (!formats.length || formats.includes(format)) return;
    // For AHB prefer a format that carries pruefids (APERAK/CONTRL have none),
    // so the single-doc view isn't empty on load. MIG formats all have content.
    const fallback =
      doctype === "ahb"
        ? formats.find((f) => (docsAhb[f]?.length ?? 0) > 0) ?? formats[0]
        : formats[0];
    setFormat(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formats.join("|")]);

  useEffect(() => {
    if (doctype !== "ahb") return;
    if (pruefids.length && !pruefids.includes(pruefid)) setPruefid(pruefids[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pruefids.join("|"), doctype]);

  // reflect doctype/format/pruefid in URL
  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("doctype", doctype);
    if (format) next.set("format", format);
    else next.delete("format");
    if (doctype === "ahb" && pruefid) next.set("pruefid", pruefid);
    else next.delete("pruefid");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctype, format, pruefid]);

  // fetch the selected doc
  const [ahbDoc, setAhbDoc] = useState<AhbDoc | null>(null);
  const [migDoc, setMigDoc] = useState<MigDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fv || !format) {
      setAhbDoc(null);
      setMigDoc(null);
      return;
    }
    if (doctype === "ahb" && !pruefid) {
      setAhbDoc(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAhbDoc(null);
    setMigDoc(null);
    const standParam = isCurrent ? undefined : effectiveStand;
    const p =
      doctype === "ahb"
        ? regelwerkApi.getAhbDoc(fv, format, pruefid, standParam).then((d) => {
            if (!cancelled) setAhbDoc(d);
          })
        : regelwerkApi.getMigDoc(fv, format, standParam).then((d) => {
            if (!cancelled) setMigDoc(d);
          });
    p.catch((e) => {
      if (!cancelled) setError(String(e));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fv, doctype, format, pruefid, effectiveStand, isCurrent]);

  const migRows = useMemo(
    () => (migDoc ? flattenMigSegments(migDoc.segments) : []),
    [migDoc],
  );

  function exportAhbCsv() {
    if (!ahbDoc) return;
    const header = [
      "Segmentgruppe", "Segmentname", "Segment", "Datenelement",
      "Qualifier", "Name", "Pflichtfeld", "Bedingung",
    ];
    const rows = ahbDoc.lines.map((l) => [
      l.segment_group_key, l.section_name, l.segment_code, l.data_element,
      l.value_pool_entry, l.name, l.ahb_expression, l.conditions,
    ]);
    downloadCsv(`ahb-${fv}-${format}-${pruefid}.csv`, header, rows);
  }

  function exportMigCsv() {
    if (!migDoc) return;
    const header = ["Ebene", "Typ", "Segment/Gruppe", "Name", "Status", "Format", "Codes"];
    const rows = migRows.map((r) => [
      String(r.level), r.kind, r.code, r.name, r.status, r.format, r.codes,
    ]);
    downloadCsv(`mig-${fv}-${format}.csv`, header, rows);
  }

  const canExport = doctype === "ahb" ? !!ahbDoc : !!migDoc;

  return (
    <>
      <Card>
        <Flex className="tabellen-toolbar" wrap="Wrap" alignItems="Center">
          <FvSelect fvs={fvs} fv={fv} setFv={setFv} />

          {staende.length > 0 && (
            <Flex className="tabellen-field" alignItems="Center">
              <label htmlFor="tab-stand">Stand</label>
              <Select
                id="tab-stand"
                style={{ minWidth: "200px" }}
                selectedKey={effectiveStand || null}
                onSelectionChange={(key) => key && setStand(String(key))}
              >
                {staende.map((s) => (
                  <SelectItem key={s.stand} id={s.stand}>
                    {s.stand === current ? `${s.label} (aktuell)` : s.label}
                  </SelectItem>
                ))}
              </Select>
            </Flex>
          )}

          <Flex className="tabellen-field" alignItems="Center">
            <label>Doctype</label>
            <SegmentedControl
              aria-label="Doctype"
              selectedKeys={new Set([doctype])}
              onSelectionChange={(keys) => {
                const first = [...keys][0];
                if (first != null) setDoctype(String(first) as Doctype);
              }}
            >
              <Segment id="ahb">AHB</Segment>
              <Segment id="mig">MIG</Segment>
            </SegmentedControl>
          </Flex>

          <Flex className="tabellen-field" alignItems="Center">
            <label htmlFor="tab-format">Format</label>
            <Select
              id="tab-format"
              style={{ minWidth: "180px" }}
              isDisabled={formats.length === 0}
              selectedKey={format || "__empty__"}
              onSelectionChange={(key) => {
                if (key && key !== "__empty__") setFormat(String(key));
              }}
            >
              {formats.length === 0
                ? [<SelectItem key="__empty__" id="__empty__">—</SelectItem>]
                : formats.map((f) => (
                    <SelectItem key={f} id={f}>
                      {f}
                    </SelectItem>
                  ))}
            </Select>
          </Flex>

          {doctype === "ahb" && (
            <Flex className="tabellen-field" alignItems="Center">
              <label htmlFor="tab-pruefid">Prüfid</label>
              <Select
                id="tab-pruefid"
                style={{ minWidth: "140px" }}
                isDisabled={pruefids.length === 0}
                selectedKey={pruefid || "__empty__"}
                onSelectionChange={(key) => {
                  if (key && key !== "__empty__") setPruefid(String(key));
                }}
              >
                {pruefids.length === 0
                  ? [<SelectItem key="__empty__" id="__empty__">—</SelectItem>]
                  : pruefids.map((p) => (
                      <SelectItem key={p} id={p}>
                        {p}
                      </SelectItem>
                    ))}
              </Select>
            </Flex>
          )}

          <div className="tabellen-spacer" />

          <Button
            variant="plain"
            isDisabled={!canExport}
            onPress={doctype === "ahb" ? exportAhbCsv : exportMigCsv}
          >
            <Icon name={princeIcon("download")} />
            CSV-Export
          </Button>
        </Flex>
      </Card>

      {error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="negative">Dokument konnte nicht geladen werden: {error}</Notice>
        </div>
      )}

      {loading && (
        <div style={{ margin: "var(--s-4)", display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
          <Spinner active size="m" />
          <span>Lade Dokument …</span>
        </div>
      )}

      {!loading && doctype === "ahb" && ahbDoc && (
        <AhbDocView doc={ahbDoc} fv={fv} format={format} />
      )}
      {!loading && doctype === "mig" && migDoc && (
        <MigDocView doc={migDoc} rows={migRows} fv={fv} format={format} />
      )}

      {!loading && !error && !canExport && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="info">
            Wähle Formatversion, Doctype{doctype === "ahb" ? ", Format und Prüfid" : " und Format"} aus.
          </Notice>
        </div>
      )}
    </>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="tabellen-meta-item">
      <div className="tabellen-meta-label">{label}</div>
      <div className="tabellen-meta-value">{value || "—"}</div>
    </div>
  );
}

/** Search field + hit count shown above a doc table (Suche in der Maske). */
function TableSearch({
  value,
  onChange,
  hits,
  total,
}: {
  value: string;
  onChange: (v: string) => void;
  hits: number;
  total: number;
}) {
  return (
    <Flex className="tabellen-tablesearch" alignItems="Center">
      <input
        className="prn-input"
        value={value}
        placeholder="In Tabelle suchen …"
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "320px", maxWidth: "100%" }}
      />
      <span className="tabellen-tablesearch-count">
        {value.trim() ? `${hits} von ${total} Zeilen` : `${total} Zeilen`}
      </span>
    </Flex>
  );
}

function AhbDocView({ doc, fv, format }: { doc: AhbDoc; fv: string; format: string }) {
  const [query, setQuery] = useState("");
  useEffect(() => setQuery(""), [fv, format, doc.pruefid]);

  const lookup = useMemo(() => makeConditionLookup(doc), [doc]);

  const lines = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return doc.lines;
    return doc.lines.filter((l) =>
      [
        l.segment_group_key, l.section_name, l.segment_code, l.data_element,
        l.value_pool_entry, l.name, l.ahb_expression, l.conditions,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [doc, query]);

  // Track section_name changes for visual section-break rows.
  let lastSection = "";
  return (
    <>
      <Card
        title={`Anwendungshandbuch ${format}`}
        style={{ marginTop: "var(--s-3)" }}
      >
        <p className="tabellen-card-subtitle">{releaseLabel(fv)}</p>
        <Flex className="tabellen-meta" wrap="Wrap">
          <MetaItem label="Format" value={format} />
          <MetaItem label="Prüfidentifikator" value={doc.pruefid} />
          <MetaItem label="EDIFACT-Format" value={doc.edifact_format} />
          <MetaItem label="Zeilen" value={String(doc.lines.length)} />
        </Flex>
      </Card>

      <Card style={{ marginTop: "var(--s-3)" }}>
        <TableSearch
          value={query}
          onChange={setQuery}
          hits={lines.length}
          total={doc.lines.length}
        />
        <div className="tabellen-table-wrap">
          <table className="fiori-data-table tabellen-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Segmentgruppe</th>
                <th style={{ width: 180 }}>Segmentname</th>
                <th style={{ width: 70 }}>Segment</th>
                <th style={{ width: 70 }}>Datenelement</th>
                <th style={{ width: 90 }}>Qualifier</th>
                <th>Name</th>
                <th style={{ width: 120 }}>Pflichtfeld</th>
                <th style={{ width: 110 }}>Bedingung/Hinweis</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const sectionChanged = l.section_name && l.section_name !== lastSection;
                if (l.section_name) lastSection = l.section_name;
                return (
                  <FragmentRow
                    key={i}
                    line={l}
                    showSection={!!sectionChanged}
                    fv={fv}
                    fmt={format}
                    pruefid={doc.pruefid}
                    lookup={lookup}
                  />
                );
              })}
              {lines.length === 0 && (
                <tr>
                  <td colSpan={8} className="tabellen-empty">
                    <span>
                      {doc.lines.length === 0
                        ? "Keine Zeilen im Dokument."
                        : "Keine Treffer für die Suche."}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function FragmentRow({
  line,
  showSection,
  fv,
  fmt,
  pruefid,
  lookup,
}: {
  line: AhbLine;
  showSection: boolean;
  fv: string;
  fmt: string;
  pruefid: string;
  lookup: (id: string) => string | undefined;
}) {
  return (
    <>
      {showSection && (
        <tr className="tabellen-section-row">
          <td colSpan={8}>{line.section_name}</td>
        </tr>
      )}
      <tr>
        <td className="mono">{line.segment_group_key}</td>
        <td>{line.section_name}</td>
        <td className="mono">{line.segment_code}</td>
        <td className="mono">{line.data_element}</td>
        <td className="mono">{line.value_pool_entry}</td>
        <td>{line.name}</td>
        <td className="mono">
          <ExprWithRefs
            expr={line.ahb_expression}
            fv={fv}
            fmt={fmt}
            pruefid={pruefid}
            lookup={lookup}
          />
        </td>
        <td className="mono">
          <ExprWithRefs
            expr={line.conditions}
            fv={fv}
            fmt={fmt}
            pruefid={pruefid}
            lookup={lookup}
          />
        </td>
      </tr>
    </>
  );
}

function MigDocView({
  doc,
  rows: allRows,
  fv,
  format,
}: {
  doc: MigDoc;
  rows: MigFlatRow[];
  fv: string;
  format: string;
}) {
  const [query, setQuery] = useState("");
  useEffect(() => setQuery(""), [fv, format]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) =>
      [r.code, r.name, r.status, r.format, r.codes, r.kind]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [allRows, query]);

  return (
    <>
      <Card
        title={`Nachrichtenbeschreibung (MIG) ${format}`}
        style={{ marginTop: "var(--s-3)" }}
      >
        <p className="tabellen-card-subtitle">{releaseLabel(fv)}</p>
        <Flex className="tabellen-meta" wrap="Wrap">
          <MetaItem label="Format" value={format} />
          <MetaItem label="EDIFACT-Format" value={doc.edifact_format} />
          <MetaItem label="Version" value={doc.version ?? ""} />
          <MetaItem label="Veröffentlichungsdatum" value={doc.veroeffentlichungsdatum ?? ""} />
          <MetaItem label="Zeilen" value={String(allRows.length)} />
        </Flex>
      </Card>

      <Card style={{ marginTop: "var(--s-3)" }}>
        <TableSearch
          value={query}
          onChange={setQuery}
          hits={rows.length}
          total={allRows.length}
        />
        <div className="tabellen-table-wrap">
          <table className="fiori-data-table tabellen-table">
            <thead>
              <tr>
                <th style={{ width: 240 }}>Segment/Gruppe</th>
                <th>Name</th>
                <th style={{ width: 80 }}>Status</th>
                <th style={{ width: 120 }}>Format</th>
                <th style={{ width: 220 }}>Codes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isSeg = r.kind === "segment" || r.kind === "group";
                return (
                  <tr key={i} className={isSeg ? "tabellen-mig-seg" : undefined}>
                    <td>
                      <span
                        className="mono"
                        style={{ paddingLeft: `${r.level * 16}px` }}
                      >
                        {isSeg && r.kind === "group" ? "▸ " : ""}
                        {r.code}
                      </span>
                    </td>
                    <td>{r.name}</td>
                    <td className="mono">{r.status}</td>
                    <td className="mono">{r.format}</td>
                    <td className="mono tabellen-codes">{r.codes}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="tabellen-empty">
                    <span>
                      {allRows.length === 0
                        ? "Keine Segmente im Dokument."
                        : "Keine Treffer für die Suche."}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Mode B — Globale Suche
// ---------------------------------------------------------------------------

function GlobalSearchMode({
  fvs,
  fv,
  setFv,
  docsAhb,
}: {
  fvs: string[];
  fv: string;
  setFv: (v: string) => void;
  docsAhb: Record<string, string[]>;
}) {
  const ahbFormats = useMemo(() => Object.keys(docsAhb).sort(), [docsAhb]);
  // Default to the first format that actually carries pruefids — many formats
  // (APERAK, CONTRL, …) have none, and defaulting to an empty one makes the
  // search look broken on load.
  const firstNonEmpty = useMemo(
    () => ahbFormats.find((f) => (docsAhb[f]?.length ?? 0) > 0) ?? ahbFormats[0] ?? "",
    [ahbFormats, docsAhb],
  );
  const [format, setFormat] = useState<string>("");

  useEffect(() => {
    if (ahbFormats.length && !ahbFormats.includes(format)) setFormat(firstNonEmpty);
    if (ahbFormats.length === 0) setFormat("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ahbFormats.join("|"), firstNonEmpty]);

  // cache: key `fv|fmt` → flat SearchRow[]
  const cacheRef = useRef<Map<string, SearchRow[]>>(new Map());
  const [rows, setRows] = useState<SearchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadCount, setLoadCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fv || !format) {
      setRows([]);
      return;
    }
    const cacheKey = `${fv}|${format}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setRows(cached);
      setError(null);
      return;
    }
    const pruefids = docsAhb[format] ?? [];
    if (pruefids.length === 0) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadCount(pruefids.length);
    setError(null);
    setRows([]);
    Promise.all(
      pruefids.map((pid) =>
        regelwerkApi
          .getAhbDoc(fv, format, pid)
          .then((doc) =>
            doc.lines.map((l) => ahbLineToSearchRow(l, fv, format, pid)),
          )
          .catch(() => [] as SearchRow[]),
      ),
    )
      .then((chunks) => {
        if (cancelled) return;
        const flat = chunks.flat();
        cacheRef.current.set(cacheKey, flat);
        setRows(flat);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fv, format, docsAhb]);

  // ---- filters ------------------------------------------------------------
  const [global, setGlobal] = useState("");
  const [fPruefid, setFPruefid] = useState("");
  const [fSg, setFSg] = useState("");
  const [fSeg, setFSeg] = useState("");
  const [fDe, setFDe] = useState("");
  const [fQual, setFQual] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fName, setFName] = useState("");
  const [fCond, setFCond] = useState("");

  const filtered = useMemo(() => {
    const g = global.trim().toLowerCase();
    const has = (hay: string, needle: string) =>
      needle === "" || hay.toLowerCase().includes(needle.trim().toLowerCase());
    return rows.filter((r) => {
      if (
        g &&
        ![
          r.pruefid, r.segment_group_key, r.segment_code, r.data_element,
          r.value_pool_entry, r.ahb_expression, r.name, r.conditions,
        ]
          .join(" ")
          .toLowerCase()
          .includes(g)
      ) {
        return false;
      }
      return (
        has(r.pruefid, fPruefid) &&
        has(r.segment_group_key, fSg) &&
        has(r.segment_code, fSeg) &&
        has(r.data_element, fDe) &&
        has(r.value_pool_entry, fQual) &&
        has(r.ahb_expression, fStatus) &&
        has(r.name, fName) &&
        has(r.conditions, fCond)
      );
    });
  }, [rows, global, fPruefid, fSg, fSeg, fDe, fQual, fStatus, fName, fCond]);

  // cap rendered rows for performance; filtering still runs over all rows
  const RENDER_CAP = 2000;
  const visible = filtered.slice(0, RENDER_CAP);

  function exportCsv() {
    const header = [
      "Formatversion", "Format", "Prüfid", "Segmentgruppe", "Segment",
      "Datenelement", "Qualifier", "AHB-Status", "Name", "Bedingung",
    ];
    const body = filtered.map((r) => [
      r.fv, r.format, r.pruefid, r.segment_group_key, r.segment_code,
      r.data_element, r.value_pool_entry, r.ahb_expression, r.name, r.conditions,
    ]);
    downloadCsv(`ahb-suche-${fv}-${format}.csv`, header, body);
  }

  return (
    <>
      <Card>
        <Flex className="tabellen-toolbar" wrap="Wrap" alignItems="Center">
          <FvSelect fvs={fvs} fv={fv} setFv={setFv} />
          <Flex className="tabellen-field" alignItems="Center">
            <label htmlFor="search-format">Format (AHB)</label>
            <Select
              id="search-format"
              style={{ minWidth: "180px" }}
              isDisabled={ahbFormats.length === 0}
              selectedKey={format || "__empty__"}
              onSelectionChange={(key) => {
                if (key && key !== "__empty__") setFormat(String(key));
              }}
            >
              {ahbFormats.length === 0
                ? [<SelectItem key="__empty__" id="__empty__">—</SelectItem>]
                : ahbFormats.map((f) => (
                    <SelectItem key={f} id={f}>
                      {f}
                    </SelectItem>
                  ))}
            </Select>
          </Flex>
          <div className="tabellen-spacer" />
          <Button
            variant="plain"
            isDisabled={filtered.length === 0}
            onPress={exportCsv}
          >
            <Icon name={princeIcon("download")} />
            CSV-Export
          </Button>
        </Flex>
      </Card>

      {error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="negative">Suche konnte nicht geladen werden: {error}</Notice>
        </div>
      )}

      {loading && (
        <Flex alignItems="Center" style={{ gap: "var(--s-3)", margin: "var(--s-4)" }}>
          <Spinner active size="m" />
          <span>lädt {loadCount} Dokumente …</span>
        </Flex>
      )}

      {!loading && (
        <Card style={{ marginTop: "var(--s-3)" }}>
          <div className="tabellen-filtergrid">
            <FilterInput label="Globale Suche" value={global} onChange={setGlobal} wide />
            <FilterInput label="Prüfid" value={fPruefid} onChange={setFPruefid} />
            <FilterInput label="Segmentgruppe" value={fSg} onChange={setFSg} />
            <FilterInput label="Segment" value={fSeg} onChange={setFSeg} />
            <FilterInput label="Datenelement" value={fDe} onChange={setFDe} />
            <FilterInput label="Qualifier" value={fQual} onChange={setFQual} />
            <FilterInput label="AHB-Status" value={fStatus} onChange={setFStatus} />
            <FilterInput label="Name" value={fName} onChange={setFName} />
            <FilterInput label="Bedingung" value={fCond} onChange={setFCond} />
          </div>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <Card title={`Ergebnisse (${filtered.length} items)`} style={{ marginTop: "var(--s-3)" }}>
          <div className="tabellen-table-wrap">
            <table className="fiori-data-table tabellen-table">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Formatversion</th>
                  <th style={{ width: 90 }}>Format</th>
                  <th style={{ width: 80 }}>Prüfid</th>
                  <th style={{ width: 90 }}>Segmentgruppe</th>
                  <th style={{ width: 70 }}>Segment</th>
                  <th style={{ width: 70 }}>Datenelement</th>
                  <th style={{ width: 90 }}>Qualifier</th>
                  <th style={{ width: 110 }}>AHB-Status</th>
                  <th>Name</th>
                  <th style={{ width: 110 }}>Bedingung</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={i}>
                    <td className="mono">{r.fv}</td>
                    <td className="mono">{r.format}</td>
                    <td className="mono">{r.pruefid}</td>
                    <td className="mono">{r.segment_group_key}</td>
                    <td className="mono">{r.segment_code}</td>
                    <td className="mono">{r.data_element}</td>
                    <td className="mono">{r.value_pool_entry}</td>
                    <td className="mono">
                      <ExprWithRefs
                        expr={r.ahb_expression}
                        fv={r.fv}
                        fmt={r.format}
                        pruefid={r.pruefid}
                      />
                    </td>
                    <td>{r.name}</td>
                    <td className="mono">
                      <ExprWithRefs
                        expr={r.conditions}
                        fv={r.fv}
                        fmt={r.format}
                        pruefid={r.pruefid}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length > RENDER_CAP && (
            <div className="tabellen-cap-hint">
              <span className="tabellen-cap-info">
                Nur die ersten {RENDER_CAP} von {filtered.length} Treffern werden angezeigt –
                Filter verfeinern oder CSV exportieren.
              </span>
            </div>
          )}
        </Card>
      )}

      {!loading && !error && rows.length === 0 && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="info">
            Wähle Formatversion und AHB-Format, um alle Prüfidentifikatoren zu durchsuchen.
          </Notice>
        </div>
      )}
    </>
  );
}

function FilterInput({
  label,
  value,
  onChange,
  wide,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  wide?: boolean;
}) {
  return (
    <Flex
      direction="Column"
      className={wide ? "tabellen-filter tabellen-filter-wide" : "tabellen-filter"}
    >
      <label>{label}</label>
      <input
        className="prn-input"
        value={value}
        placeholder="Filter …"
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%" }}
      />
    </Flex>
  );
}

export default TabellenPage;
